import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  compoundFactor,
  getCalendarPolicyYear,
  getDailyBenefitAmountForEnvironment,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import { deriveIneligibilityReason } from '../deriveIneligibilityReason';

// ---------------------------------------------------------------------------
// Lincoln MoneyGuard Fixed Advantage — asset-based hybrid universal life with
// a long-term care benefits rider (LTCBR).
//
// Structurally identical to OneAmerica Asset Care: a two-pool reimbursement
// hybrid drawn Face-first then Continuation-of-Benefits.
//
//   Pool 1 (base) = Face / Specified Amount of Death Benefit
//                   (`policyMaximumBenefitAmount`). On hybrids this field is
//                   the death benefit, which on this product also equals the
//                   LTC acceleration portion (no NYL-ACIO-style face↔accel
//                   divergence because `hasFlatDeathBenefit` is true).
//                   `policyBenefitUtilizedToDate` is subtracted at init.
//   Pool 2 (eob)  = Continuation of Benefits (`policyContinuationBenefitAmount`),
//                   drawn only after the Face pool exhausts.
//
// Death benefit: flat Face (`hasFlatDeathBenefit = true` → never inflated).
// LTC reimbursements reduce the death benefit dollar-for-dollar by the FACE
// (base) drawdown only — COB drawdown does NOT reduce the death benefit.
// Floored at the Residual Death Benefit (`policyMinimumGuaranteedBenefitAmount`,
// 5% of Face on this illustration).
//
// Payout model (default reimbursement, indemnity at first-claim election):
//   - Reimbursement: pay min(eligibleCost, proRatedCap), drawn Face then COB.
//   - Indemnity (gated on `isIndemnityPolicyPayment`): mirror OA Asset Care —
//     pay the capped indemnity from the Face pool regardless of actual cost,
//     falling back to reimbursement on COB once Face is exhausted.
//
//     INDEMNITY BENEFIT REDUCTION: Lincoln caps cash indemnity at 80% of the
//     Monthly Maximum ("The indemnity choice option provides 80% of the
//     Maximum Monthly LTC Benefit Amount", illustration p.4 fn 5 / p.9). Like
//     Asset Care's 75% cap, this is a fixed product property carried on the
//     policy as `policyIndemnityBenefitPercent` (the upload field map fixes it
//     at 0.80 — see policyUploadPolicyFieldMap.ts), which the indemnity branch
//     below applies. The legacy aggregate calc applies no such factor, so an
//     uploaded indemnity MoneyGuard diverges from legacy here — the new
//     strategy is the intended-correct path. Default is reimbursement.
//
// Inflation: per-pool compound at policy anniversary, accrued on the at-issue
// amount, gated on pool > 0. Rates: face/base from
// `policyInflationProtectionOnFaceAmount ?? policyInflationProtection`, COB/eob
// from `policyInflationProtectionOnCOBAmount ?? policyInflationProtection`, cap
// `policyInflationProtectionYears`. The example illustration elected no
// inflation ("LTCBR Inflation Option: n/a") so every factor is 1.0 there, but
// the general inflation path is implemented like OA Asset Care.
//
// Elimination period: 0 days on this product. Served via the two-tier WP
// fields; EP days are out of pocket with no retroactive lump.
// `policyBenefitPeriodMonths` (LTCBR Benefit Duration) is NOT a cap — the
// dollar pools are the cap.
//
// Like OA Asset Care, MoneyGuard needs no per-claim or per-month carry-forward
// beyond the universal pools/waitingPeriod/facts/deltas — both the state and
// deltas slots are empty.
// ---------------------------------------------------------------------------

export type LincolnMoneyGuardState = Record<string, never>;
export type LincolnMoneyGuardDeltas = Record<string, never>;

interface InflationConfig {
  baseRate: number;
  eobRate: number;
  capYears: number;
}

// Each pool reads its own inflation rate, falling back to the policy-wide rate
// when the pool-specific knob is missing. Mirrors OA Asset Care.
function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const policyWide = ltcPolicy.policyInflationProtection ?? 0;
  return {
    baseRate: ltcPolicy.policyInflationProtectionOnFaceAmount ?? policyWide,
    eobRate: ltcPolicy.policyInflationProtectionOnCOBAmount ?? policyWide,
    capYears: ltcPolicy.policyInflationProtectionYears ?? 0,
  };
}

function readAtIssuePools(ltcPolicy: PolicyFundingSource): {
  baseAtIssue: number;
  eobAtIssue: number;
} {
  return {
    baseAtIssue: ltcPolicy.policyMaximumBenefitAmount ?? 0,
    eobAtIssue: ltcPolicy.policyContinuationBenefitAmount ?? 0,
  };
}

export const lincolnMoneyGuardStrategy: PolicyProjectionStrategy<
  LincolnMoneyGuardState,
  LincolnMoneyGuardDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<LincolnMoneyGuardState> {
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);
    // Anniversary inflation compounds every policy year regardless of whether
    // a claim has started; the projection only spans claim months, so
    // anniversaries between policy issue and claim start would otherwise be
    // silently skipped. Pre-inflate at claim start so opening pools match the
    // carrier's actual current pool. Claims starting at policy year 1 keep
    // both factors at 1.0; on the 0%-inflation illustration both factors are
    // always 1.0 regardless of claim-start year.
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart =
      baseAtIssue * compoundFactor(baseRate, capYears, claimStartPolicyYear);
    const eobAtClaimStart =
      eobAtIssue * compoundFactor(eobRate, capYears, claimStartPolicyYear);
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - utilizedToDate, 0),
        extensionOfBenefitsPool: eobAtClaimStart,
        deathBenefit: Math.max(faceAmount, minGuaranteed),
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {},
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = clientAsClient.clientAddedDateTime
      ? clientAsClient.clientAddedDateTime.getFullYear()
      : new Date().getFullYear();
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    return {
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish), misrepresenting the cap; the helper honors explicit 0
      // and falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // MoneyGuard has no single product-wide inflation knob distinct from the
      // per-pool ones; populate inflationRate from the face/base rate for
      // legacy/UI compatibility.
      inflationRate: baseRate,
      inflationCapYears: capYears,
      baseBenefitPoolInflationRate: baseRate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: eobRate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      // MoneyGuard ships dollar pools (Face Amount, Continuation of Benefits),
      // not a duration-derived model, so the duration fields stay unset and
      // the UI does not render a misleading "× N months" derivation.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear, calendarYearOffset } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);

    const baseFactor = compoundFactor(baseRate, capYears, policyYear);
    const eobFactor = compoundFactor(eobRate, capYears, policyYear);

    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * baseFactor;
    const inflatedAcceleration = baseAtIssue * baseFactor;
    const inflatedEob = eobAtIssue * eobFactor;
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    return {
      policyYear,
      calendarYearOffset,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedAcceleration + inflatedEob,
      monthlyBenefitCap: inflatedBaseMonthly,
      theoreticalLtcPoolCeiling: inflatedAcceleration + inflatedEob,
    };
  },

  computeMonth(
    input: MonthComputeInput<LincolnMoneyGuardState, LincolnMoneyGuardDeltas>,
  ): MonthlyEntry<LincolnMoneyGuardState, LincolnMoneyGuardDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    // MoneyGuard's example illustration has a 0-day elimination period.
    // Routed by care environment through the two-tier WP fields like OA Asset
    // Care; with 0-day WPs this produces no waiting days.
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Cost metrics + per-month dollar cap (this year's face/base factor).
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );

    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);
    const calendarYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const monthlyCap = baseMonthly * compoundFactor(baseRate, capYears, policyYear);

    // 3. Annual anniversary accrual against the AT-ISSUE amount, gated on
    //    "pool still > 0" so an exhausted pool stays exhausted (the carrier
    //    caps lifetime LTC at what was drawn before exhaustion; inflated
    //    ceilings don't reopen a closed pool).
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        compoundFactor(baseRate, capYears, policyYear) -
        compoundFactor(baseRate, capYears, priorYear);
      const eobFactorDelta =
        compoundFactor(eobRate, capYears, policyYear) -
        compoundFactor(eobRate, capYears, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseAtIssue * baseFactorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        eobInflationAccrual = eobAtIssue * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual =
      openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Payout: branches on indemnity vs. reimbursement. Indemnity is
    //    Face-only — once the Face pool is exhausted we revert to
    //    reimbursement on COB (mirrors OA Asset Care).
    //
    //    INDEMNITY BENEFIT REDUCTION: Lincoln caps cash indemnity at 80% of the
    //    Monthly Maximum (illustration p.4 fn 5 / p.9). Like Asset Care's 75%
    //    cap, this is a fixed product property carried on the policy as
    //    `policyIndemnityBenefitPercent` (the upload field map fixes it at 0.80
    //    — see policyUploadPolicyFieldMap.ts). The strategy is product-agnostic:
    //    it applies whatever percent the policy carries, defaulting to 1.0 when
    //    absent. So an uploaded MoneyGuard pays 80% under an indemnity election;
    //    a hand-entered policy with no percent pays 100% until one is set.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const indemnityPercent = ltcPolicy.policyIndemnityBenefitPercent ?? 1;
    const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromBase = 0;
    let drawFromEob = 0;
    let indemnityPaid = 0;
    if (isIndemnity && baseAfterAccrual > 0) {
      // Pay capped indemnity from the Face pool regardless of actual cost.
      // Drawdown cannot exceed the pool itself (last Face month may pay less
      // than the capped amount).
      const indemnityAmount = Math.min(proRatedCap * indemnityPercent, baseAfterAccrual);
      drawFromBase = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      // Reimbursement, or indemnity-with-Face-exhausted. Pay
      // min(eligibleCost, proRatedCap), drawn Face first then COB.
      const wouldPay = Math.min(eligibleCost, proRatedCap);
      drawFromBase = Math.min(wouldPay, baseAfterAccrual);
      drawFromEob = Math.min(wouldPay - drawFromBase, eobAfterAccrual);
    }
    const ltcCoveragePaid = drawFromBase + drawFromEob;

    // 5. Closing balances. Only Face (base) drawdown depletes the death
    //    benefit (COB is a separate pool and doesn't accelerate the face).
    //    Floor at the Residual Death Benefit.
    const closeBase = baseAfterAccrual - drawFromBase;
    const closeEob = eobAfterAccrual - drawFromEob;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const closeDeathBenefit = Math.max(
      openState.pools.deathBenefit - drawFromBase,
      minGuaranteed,
    );
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && drawFromBase > deathBenefitReduction;

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      calendarYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: ltcCoveragePaid,
      ineligibleCost: costMetrics.effectiveCareCost - ltcCoveragePaid,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<LincolnMoneyGuardDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {},
    };

    const closeState: MonthlyState<LincolnMoneyGuardState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {},
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<
      LincolnMoneyGuardState,
      LincolnMoneyGuardDeltas
    >({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityReason = deriveIneligibilityReason<LincolnMoneyGuardState>({
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    });

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason },
    };
  },

  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<LincolnMoneyGuardState>): number {
    return closingState.pools.deathBenefit;
  },
};
